import React, { useEffect, useRef, useState } from "react";
import { uploadFile } from "../../firebase/fileUpload";
import EventDataSection from "./child/EventDataSection";
import Winners from "./child/Winners";
import Entities from "./child/Entities";
import CoinValue from "./child/coinSystem/CoinValue";
import ReferralCoins from "./child/coinSystem/RrCoins";
import Category from "./child/Category";
import ParticipationAdditionalData from "./child/ParticipationAdditionalData";
import VotesValue from "./child/VotesValue";

/**
 * EventAdditionalData (with Wizard)
 * - One-time hydration from parent additionalData to avoid re-render loops.
 * - setAdditionalData keeps parent state in sync to persist data across steps.
 * - onFinish() (prop) is called when all steps are valid and user clicks Finish.
 */

const EventAdditionalData = ({
  additionalData = {},
  setAdditionalData,
  isEditMode,
  onFinish,
  templateData = null,
  onTemplateDataChange = null,
}) => {
  // ---------- Defaults (schema preserved) ----------
  const defaultAdditionalData = {
    votes: {
      like: { votes: 10, value: 1 },
      comment: { votes: 5, value: 1 },
      share: { votes: 2, value: 1 },
      repost: { votes: 1, value: 1 },
      impression: { votes: 100, value: 1 },
      reach: { votes: 100, value: 1 },
      save: { votes: 1, value: 1 },
    },
    winners: { national: 0, state: 0, city: 0, pincode: 0, international: 0 },
    entities: [],
    coinValue: 1,
    referredCoins: {
      sponsorTosponsor: 0,
      sponsorTouser: 0,
      userTouser: 0,
      userTosponsor: 0,
    },
    referrerCoins: {
      sponsorTosponsor: 0,
      sponsorTouser: 0,
      userTouser: 0,
      userTosponsor: 0,
    },
    generalCategory: [],
    ageCategory: [],
    followersCategory: [],
    genderCategory: [],
    isHashtagsEditable: false,
    isCaptionEditable: false,
    isSponsorTagsEditable: false,
    eventPrice: 0,
    eventCaption1: "",
    eventCaption2: "",
    eventHashtags: [],
    eventImageUrl: null,
    eventAdditionalImages: [],
    eventAdditionalDesc: "",
    advideoUrl: null,
    demoVideoUrl: null,
    participationPost: {
      caption1: "",
      caption2: "",
      hashtags: [],
      frameImage: null,
      carouselImages: [],
      isPPHashtagEditable: false,
      isPPCaptionEditable: false,
      arePPSponsorsTagEditable: false,
      isDefaultChecked: false,
    },
    // Carousel Configurations
    isParticipationCarousel: false,
    participationCarouselConfig: {
      adminMediaCount: 0,
      userMediaCount: 0,
      mediaItems: [],
    },
  };

  // ---------- Local State ----------
  // Helper: ensure a value is always a plain array, even when the API
  // serialises it as a JSON string or accidentally sends an object/null.
  const toArray = (val, fallback = []) => {
    if (Array.isArray(val)) return val;
    if (typeof val === "string") {
      try { const p = JSON.parse(val); return Array.isArray(p) ? p : fallback; } catch { return fallback; }
    }
    return fallback;
  };

  // Merge defaults with the incoming prop immediately so the very first render
  // has the correct values.  This eliminates the stale-empty window that existed
  // when state was initialised from hardcoded defaults and then hydrated async.
  const initData = { ...defaultAdditionalData, ...(additionalData || {}) };
  const initPP = {
    ...defaultAdditionalData.participationPost,
    ...((additionalData?.participationPost) || {}),
  };

  const [winners, setWinners] = useState(initData.winners || defaultAdditionalData.winners);
  const [entities, setEntities] = useState(toArray(initData.entities));
  const [coinValue, setCoinValue] = useState(initData.coinValue ?? defaultAdditionalData.coinValue);
  const [isStep1Uploading, setIsStep1Uploading] = useState(false);
  const [isStep2Uploading, setIsStep2Uploading] = useState(false);

  const [votes, setVotes] = useState({ ...defaultAdditionalData.votes, ...(initData.votes || {}) });
  const [referredCoins, setReferredCoins] = useState(initData.referredCoins || defaultAdditionalData.referredCoins);
  const [referrerCoins, setReferrerCoins] = useState(initData.referrerCoins || defaultAdditionalData.referrerCoins);
  const [generalCategory, setGeneralCategory] = useState(toArray(initData.generalCategory));
  const [ageCategory, setAgeCategory] = useState(toArray(initData.ageCategory));
  const [followersCategory, setFollowersCategory] = useState(toArray(initData.followersCategory));
  const [genderCategory, setGenderCategory] = useState(toArray(initData.genderCategory));
  const [isHashtagsEditable, setIsHashtagsEditable] = useState(!!initData.isHashtagsEditable);
  const [isCaptionEditable, setIsCaptionEditable] = useState(!!initData.isCaptionEditable);
  const [isSponsorTagsEditable, setIsSponsorTagsEditable] = useState(!!initData.isSponsorTagsEditable);
  const [eventPrice, setEventPrice] = useState(initData.eventPrice ?? defaultAdditionalData.eventPrice);
  const [eventCaption1, setEventCaption1] = useState(initData.eventCaption1 || "");
  const [eventCaption2, setEventCaption2] = useState(initData.eventCaption2 || "");
  const [eventHashtags, setEventHashtags] = useState(toArray(initData.eventHashtags));
  const [eventImageUrl, setEventImageUrl] = useState(initData.eventImageUrl || null);
  const [eventAdditionalImages, setEventAdditionalImages] = useState(toArray(initData.eventAdditionalImages));
  const [eventAdditionalDesc, setEventAdditionalDesc] = useState(initData.eventAdditionalDesc || "");
  const [advideoUrl, setAdVideoUrl] = useState(initData.advideoUrl || null);
  const [demoVideoUrl, setDemoVideoUrl] = useState(initData.demoVideoUrl || null);

  // Carousel Configuration State
  const [isParticipationCarousel, setIsParticipationCarousel] = useState(initData.isParticipationCarousel);
  const [participationCarouselConfig, setParticipationCarouselConfig] = useState(initData.participationCarouselConfig);

  // Participation Data
  const [ppCaption1, setPpCaption1] = useState(initPP.caption1 || "");
  const [ppCaption2, setPpCaption2] = useState(initPP.caption2 || "");
  const [ppHashtags, setPpHashtags] = useState(toArray(initPP.hashtags));
  const [newHashtag, setNewHashtag] = useState("");
  const [frameImageUrl, setFrameImageUrl] = useState(initPP.frameImage || null);
  const [participationCarouselImages, setParticipationCarouselImages] = useState(toArray(initPP.carouselImages));
  const [isPPHashtagEditable, setIsPPHashtagEditable] = useState(!!initPP.isPPHashtagEditable);
  const [isPPCaptionEditable, setIsPPCaptionEditable] = useState(!!initPP.isPPCaptionEditable);
  const [arePPSponsorsTagEditable, setArePPSponsorsTagEditable] = useState(!!initPP.arePPSponsorsTagEditable);
  const [isDefaultChecked, setIsDefaultChecked] = useState(!!initPP.isDefaultChecked);

  // ---------- Wizard State ----------
  const [currentStep, setCurrentStep] = useState(1);
  const stepLabels = [
    "Event Media Share",
    "Participation",
    "Winners",
    "Categories",
    "Coins & Votes",
  ];
  const totalSteps = stepLabels.length;

  // ---------- One-time Hydration to prevent loops ----------
  // hydratedRef starts TRUE because we already initialise state from initData
  const hydratedRef = useRef(true);
  const prevAdditionalDataStringRef = useRef(null);
  // Initialise prevRef to the current prop JSON on first mount so the hydration
  // effect sees dataChanged=false and returns early (no re-hydration needed).
  if (prevAdditionalDataStringRef.current === null) {
    prevAdditionalDataStringRef.current = JSON.stringify(additionalData || {});
  }
  const prevIsEditModeRef = useRef(isEditMode);
  const [justHydrated, setJustHydrated] = useState(false);
  // Tracks the last payload sent via setAdditionalData so the sync effect
  // can skip firing when nothing has actually changed (breaks render loop).
  const lastSyncedDataRef = useRef(null);

  const hasRealData = (obj) => {
    if (!obj) return false;
    // consider "real" if any of these are non-default
    return Boolean(
      (obj.eventCaption1 && obj.eventCaption1.length) ||
      (obj.eventCaption2 && obj.eventCaption2.length) ||
      (obj.eventHashtags && obj.eventHashtags.length) ||
      (obj.participationPost &&
        (obj.participationPost.caption1 ||
          obj.participationPost.caption2 ||
          (obj.participationPost.hashtags || []).length))
    );
  };

  useEffect(() => {
    // Use JSON string comparison to detect MEANINGFUL changes (handles draft restoration)
    // Only hydrate if data substantially changed, to prevent infinite loops
    const currentDataString = JSON.stringify(additionalData);
    const dataChanged = prevAdditionalDataStringRef.current !== currentDataString;

    // Check if data NOW has real content (was empty before, now has content)
    const hasRealDataNow = hasRealData(additionalData);
    let hadRealDataBefore = false;
    if (prevAdditionalDataStringRef.current) {
      try {
        const prevData = JSON.parse(prevAdditionalDataStringRef.current);
        hadRealDataBefore = (prevData.eventPrice > 0) || !!prevData.eventCaption1;
      } catch (e) {
        hadRealDataBefore = false;
      }
    }
    const dataBecomesReal = !hadRealDataBefore && hasRealDataNow;
    const dataBecamesEmpty = hadRealDataBefore && !hasRealDataNow; // Data went from real to empty - SKIP this

    // Reset hydration flag if:
    // 1. Switching to edit mode, OR
    // 2. Data becomes populated (was empty, now has content)
    if ((prevIsEditModeRef.current !== isEditMode && isEditMode) || dataBecomesReal) {
      hydratedRef.current = false;
      prevIsEditModeRef.current = isEditMode;
    }

    // PREVENT re-hydration if data went from real to empty (avoid feedback loop from sync-back)
    if (dataBecamesEmpty) {
      return;
    }

    // Only allow re-hydration on substantial changes, not on every render
    if (hydratedRef.current && !dataChanged && !dataBecomesReal) return;

    // Hydrate if: edit mode AND data has real content
    if ((isEditMode || hasRealData(additionalData)) && !dataBecamesEmpty) {
      const updated = { ...defaultAdditionalData, ...additionalData };

      // Batch all state updates to minimize re-renders
      // Merge with default votes to ensure new keys (e.g. repost) appear for old events
      setVotes({ ...defaultAdditionalData.votes, ...(updated.votes || {}) });
      setWinners(updated.winners || defaultAdditionalData.winners);
      setEntities(toArray(updated.entities));
      setCoinValue(updated.coinValue ?? defaultAdditionalData.coinValue);
      setReferredCoins(updated.referredCoins || defaultAdditionalData.referredCoins);
      setReferrerCoins(updated.referrerCoins || defaultAdditionalData.referrerCoins);
      setGeneralCategory(toArray(updated.generalCategory));
      setAgeCategory(toArray(updated.ageCategory));
      setFollowersCategory(toArray(updated.followersCategory));
      setGenderCategory(toArray(updated.genderCategory));
      setIsHashtagsEditable(!!updated.isHashtagsEditable);
      setIsCaptionEditable(!!updated.isCaptionEditable);
      setIsSponsorTagsEditable(!!updated.isSponsorTagsEditable);
      setEventPrice(updated.eventPrice ?? defaultAdditionalData.eventPrice);
      setEventCaption1(updated.eventCaption1 || "");
      setEventCaption2(updated.eventCaption2 || "");
      setEventHashtags(toArray(updated.eventHashtags));
      setEventImageUrl(updated.eventImageUrl || null);
      setEventAdditionalImages(toArray(updated.eventAdditionalImages));
      setEventAdditionalDesc(updated.eventAdditionalDesc || "");
      setAdVideoUrl(updated.advideoUrl || null);
      setDemoVideoUrl(updated.demoVideoUrl || null);

      // Carousel configurations
      setIsParticipationCarousel(!!updated.isParticipationCarousel);
      setParticipationCarouselConfig(updated.participationCarouselConfig || defaultAdditionalData.participationCarouselConfig);

      const pp = updated.participationPost || {};
      setPpCaption1(pp.caption1 || "");
      setPpCaption2(pp.caption2 || "");
      setPpHashtags(toArray(pp.hashtags));
      setFrameImageUrl(pp.frameImage || null);
      setParticipationCarouselImages(toArray(pp.carouselImages));
      setIsPPHashtagEditable(!!pp.isPPHashtagEditable);
      setIsPPCaptionEditable(!!pp.isPPCaptionEditable);
      setArePPSponsorsTagEditable(!!pp.arePPSponsorsTagEditable);
      setIsDefaultChecked(!!pp.isDefaultChecked);

      hydratedRef.current = true;
      prevAdditionalDataStringRef.current = currentDataString;
      setJustHydrated(true);
    }
  }, [isEditMode, additionalData]); // Proper dependency to detect draft restoration

  // Reset hydration flag after one frame
  useEffect(() => {
    if (justHydrated) {
      const timeout = setTimeout(() => setJustHydrated(false), 0);
      return () => clearTimeout(timeout);
    }
  }, [justHydrated]);

  // ---------- Sync up to parent (persist across steps) ----------
  useEffect(() => {
    // Don't sync immediately after hydration to prevent loops, 
    // and don't sync if we haven't successfully hydrated real data from props yet in edit mode.
    if (justHydrated || (!hydratedRef.current && isEditMode)) return;

    const payload = {
      votes,
      winners,
      entities,
      coinValue,
      referredCoins,
      referrerCoins,
      generalCategory,
      ageCategory,
      followersCategory,
      genderCategory,
      isHashtagsEditable,
      isCaptionEditable,
      isSponsorTagsEditable,
      eventPrice,
      eventCaption1,
      eventCaption2,
      eventHashtags,
      eventImageUrl,
      eventAdditionalImages,
      eventAdditionalDesc,
      advideoUrl,
      demoVideoUrl,
      participationPost: {
        caption1: ppCaption1,
        caption2: ppCaption2,
        hashtags: ppHashtags,
        frameImage: frameImageUrl,
        carouselImages: participationCarouselImages,
        isPPHashtagEditable,
        isPPCaptionEditable,
        arePPSponsorsTagEditable,
        isDefaultChecked,
      },
      // Carousel configurations
      isParticipationCarousel,
      participationCarouselConfig,
    };

    // Skip if payload content hasn't changed — prevents the parent→prop→hydration→sync loop
    const payloadStr = JSON.stringify(payload);
    if (lastSyncedDataRef.current === payloadStr) return;
    lastSyncedDataRef.current = payloadStr;

    setAdditionalData(payload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [justHydrated,
    votes,
    winners,
    entities,
    coinValue,
    referredCoins,
    referrerCoins,
    generalCategory,
    ageCategory,
    followersCategory,
    genderCategory,
    isHashtagsEditable,
    isCaptionEditable,
    isSponsorTagsEditable,
    eventPrice,
    eventCaption1,
    eventCaption2,
    eventHashtags,
    eventImageUrl,
    eventAdditionalImages,
    eventAdditionalDesc,
    advideoUrl,
    demoVideoUrl,
    ppCaption1,
    ppCaption2,
    ppHashtags,
    frameImageUrl,
    participationCarouselImages,
    isPPHashtagEditable,
    isPPCaptionEditable,
    arePPSponsorsTagEditable,
    isDefaultChecked,
    isParticipationCarousel,
    participationCarouselConfig,
  ]);

  // ---------- File upload wrapper ----------
  const handleFileUpload = async (file) => {
    try {
      if (!file) throw new Error("No file provided");
      const uploadResponse = await uploadFile(file);
      return uploadResponse;
    } catch (error) {
      console.error("File upload failed:", error);
      return null;
    }
  };

  // ---------- Validation & Wizard ----------
  const [errors, setErrors] = useState({});

  const scrollToFirstError = () => {
    setTimeout(() => {
      const errorEl = document.querySelector('.error-field');
      if (errorEl) {
        errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!eventCaption2?.trim()) newErrors.eventCaption2 = true;
    // eventHashtags validation removed — hashtags moved to Participation tab

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      scrollToFirstError();
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!ppCaption1?.trim()) newErrors.ppCaption1 = true;

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      scrollToFirstError();
      return false;
    }
    return true;
  };

  const validateStep3 = () => true; // winners/entities can be empty unless you want stricter rules
  const validateStep4 = () => true; // categories optional unless required
  const validateStep5 = () => true; // coins & votes optional unless required

  const nextStep = () => {
    if (currentStep === 1 && !validateStep1()) return;
    if (currentStep === 2 && !validateStep2()) return;
    if (currentStep === 3 && !validateStep3()) return;
    if (currentStep === 4 && !validateStep4()) return;
    setCurrentStep((s) => Math.min(s + 1, totalSteps));
  };

  const prevStep = () => setCurrentStep((s) => Math.max(s - 1, 1));

  const goToStep = (n) => {
    setCurrentStep(n);
  };

  const finish = () => {
    if (!validateStep5()) return;
    // Final payload already synced to parent via setAdditionalData.
    if (typeof onFinish === "function") onFinish();
  };

  const StepPill = ({ index, label }) => {
    const active = currentStep === index;
    const done = currentStep > index;
    return (
      <button
        type="button"
        onClick={() => goToStep(index)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs border transition
        ${active
            ? "bg-blue-600 text-white border-blue-600 cursor-default"
            : done
              ? "bg-green-50 text-green-700 border-green-300 hover:bg-green-100 cursor-pointer"
              : "bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100 cursor-pointer"
          }`}
      >
        <span
          className={`inline-block w-2 h-2 rounded-full ${active ? "bg-white" : done ? "bg-green-600" : "bg-gray-500"
            }`}
        />
        <span className="font-medium">{label}</span>
      </button>
    );
  };

  // ---------- Step renderers ----------

  const renderStep1 = () => (
    <EventDataSection
      handleFileUpload={handleFileUpload}
      eventCaption2={eventCaption2}
      setEventCaption2={setEventCaption2}
      eventHashtags={eventHashtags}
      setEventHashtags={setEventHashtags}
      eventImageUrl={eventImageUrl}
      setEventImageUrl={setEventImageUrl}
      eventAdditionalImages={eventAdditionalImages}
      setEventAdditionalImages={setEventAdditionalImages}
      eventAdditionalDesc={eventAdditionalDesc}
      setEventAdditionalDesc={setEventAdditionalDesc}
      advideoUrl={advideoUrl}
      setAdVideoUrl={setAdVideoUrl}
      demoVideoUrl={demoVideoUrl}
      setDemoVideoUrl={setDemoVideoUrl}
      isEditMode={isEditMode}
      onUploadingChange={setIsStep1Uploading} // <<< NEW
      errors={errors}
      setErrors={setErrors}
    />
  );

  const renderStep2 = () => (
    <ParticipationAdditionalData
      ppCaption1={ppCaption1}
      setPpCaption1={setPpCaption1}
      ppCaption2={ppCaption2}
      setPpCaption2={setPpCaption2}
      ppHashtags={ppHashtags}
      setPpHashtags={setPpHashtags}
      newHashtag={newHashtag}
      setNewHashtag={setNewHashtag}
      frameImageUrl={frameImageUrl}
      setFrameImageUrl={setFrameImageUrl}
      participationCarouselImages={participationCarouselImages}
      setParticipationCarouselImages={setParticipationCarouselImages}
      isPPHashtagEditable={isPPHashtagEditable}
      setIsPPHashtagEditable={(val) => {
        setIsPPHashtagEditable(val);
        setIsHashtagsEditable(val);
      }}
      isPPCaptionEditable={isPPCaptionEditable}
      setIsPPCaptionEditable={(val) => {
        setIsPPCaptionEditable(val);
        setIsCaptionEditable(val);
      }}
      arePPSponsorsTagEditable={arePPSponsorsTagEditable}
      setArePPSponsorsTagEditable={setArePPSponsorsTagEditable}
      isDefaultChecked={isDefaultChecked}
      setIsDefaultChecked={setIsDefaultChecked}
      handleFileUpload={handleFileUpload}
      isEditMode={isEditMode}
      onUploadingChange={setIsStep2Uploading}
      templateData={templateData}
      onTemplateDataChange={onTemplateDataChange}
      errors={errors}
      setErrors={setErrors}
      // Carousel Props
      isParticipationCarousel={isParticipationCarousel}
      setIsParticipationCarousel={setIsParticipationCarousel}
      participationCarouselConfig={participationCarouselConfig}
      setParticipationCarouselConfig={setParticipationCarouselConfig}
    />
  );

  const renderStep3 = () => (
    <div className="grid grid-cols-1 gap-6">
      <div className="p-4 border rounded-md bg-white shadow">
        <Winners
          winners={winners}
          setWinners={setWinners}
          isEditMode={isEditMode}
        />
      </div>
      {/* Hide Entities component from Admin Form by request
      <div className="p-4 border rounded-md bg-white shadow">
        <Entities
          entities={entities}
          setEntities={setEntities}
          isEditMode={isEditMode}
        />
      </div>
      */}
    </div>
  );

  const renderStep4 = () => (
    <>
      <Category
        title="General Category"
        categories={generalCategory}
        setCategories={setGeneralCategory}
        isEditMode={isEditMode}
        commaAutoCreate
      />
      <Category
        title="Age Category"
        categories={ageCategory}
        setCategories={setAgeCategory}
        isEditMode={isEditMode}
        autoFillNext
      />
      <Category
        title="Followers Count Category"
        categories={followersCategory}
        setCategories={setFollowersCategory}
        isEditMode={isEditMode}
        autoFillNext
      />
      {/* Gender Category — dropdown (All/Male/Female/Others/Open). Was
          accidentally dropped from the render while state/load/save stayed
          wired, so the field vanished from both create & edit. Restored
          2026-06-07. Consumed by the user app at participation step 2. */}
      <Category
        title="Gender Category"
        categories={genderCategory}
        setCategories={setGenderCategory}
        options={["All", "Male", "Female", "Others", "Open"]}
        isDropdown
        isEditMode={isEditMode}
      />
    </>
  );

  const renderStep5 = () => (
    <div className="grid grid-cols-1 gap-6">
      <div className="p-4 border rounded-md bg-white shadow">
        <CoinValue coinValue={coinValue} setCoinValue={setCoinValue} />
      </div>
      <div className="p-4 border rounded-md bg-white shadow">
        <VotesValue votes={votes} setVotes={setVotes} />
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      case 4:
        return renderStep4();
      case 5:
      default:
        return renderStep5();
    }
  };

  // ---------- UI ----------
  return (
    <div className="p-4 border rounded-md mt-4 mb-10 bg-white shadow-xl">
      <h2 className="text-xl lg:text-2xl font-bold text-blue-600 mb-6">
        {isEditMode
          ? "Edit Event Additional Data"
          : "Add Event Additional Data"}
      </h2>

      {/* Stepper */}
      <div className="w-full mb-4">
        <div className="flex flex-wrap gap-2">
          {stepLabels.map((label, idx) => (
            <StepPill key={label} index={idx + 1} label={label} />
          ))}
        </div>
        <div className="w-full h-1 bg-gray-200 rounded mt-3">
          <div
            className="h-1 bg-blue-600 rounded transition-all"
            style={{ width: `${(currentStep / totalSteps) * 100}%` }}
          />
        </div>
      </div>

      {/* Step Body */}
      <div className="mt-4">{renderCurrentStep()}</div>

      {/* Controls */}
      <div className="mt-8 flex items-center justify-between">
        <button
          type="button"
          onClick={prevStep}
          className={`px-4 py-2 rounded border ${currentStep === 1
            ? "opacity-50 cursor-not-allowed"
            : "hover:bg-gray-50"
            }`}
          disabled={currentStep === 1}
        >
          Back
        </button>

        <div className="flex items-center gap-2 text-sm text-gray-500">
          <span>
            Step {currentStep} of {totalSteps}
          </span>
        </div>

        <button
          type="button"
          onClick={currentStep < totalSteps ? nextStep : finish}
          disabled={isStep1Uploading && currentStep === 1} // <<< gate only on step 1
          className={`px-4 py-2 rounded text-white transition ${currentStep === totalSteps
            ? "bg-green-600 hover:bg-green-700"
            : "bg-blue-600 hover:bg-blue-700"
            } ${isStep1Uploading && currentStep === 1
              ? "opacity-50 cursor-not-allowed"
              : ""
            }
          ${isStep2Uploading && currentStep === 2
              ? "opacity-50 cursor-not-allowed"
              : ""
            }
          
          `}
        >
          {currentStep === totalSteps ? "Finish" : "Next"}
        </button>
      </div>
    </div>
  );
};

export default EventAdditionalData;

// old components using before 08/10

// import React, { useEffect, useState, useRef, useCallback } from "react";
// import { uploadFile } from "../../firebase/fileUpload";
// import EventDataSection from "./child/EventDataSection";
// import Winners from "./child/Winners";
// import Entities from "./child/Entities";
// import CoinValue from "./child/coinSystem/CoinValue";
// import ReferralCoins from "./child/coinSystem/RrCoins";
// import Category from "./child/Category";
// import ParticipationAdditionalData from "./child/ParticipationAdditionalData";
// import VotesValue from "./child/VotesValue";

// const EventAdditionalData = ({
//   additionalData = {}, // Default to an empty object
//   setAdditionalData,
//   isEditMode,
// }) => {
//   // Default values to prevent null/undefined errors
//   const defaultAdditionalData = {
//     votes: {
//       like: { votes: 0, value: 0 },
//       comment: { votes: 0, value: 0 },
//       share: { votes: 0, value: 0 },
//       impression: { votes: 0, value: 0 },
//       reach: { votes: 0, value: 0 },
//       save: { votes: 0, value: 0 },
//     },
//     winners: {
//       national: 0,
//       state: 0,
//       city: 0,
//       pincode: 0,
//       international: 0,
//     },
//     entities: [],
//     coinValue: 0,
//     referredCoins: {
//       sponsorTosponsor: 0,
//       sponsorTouser: 0,
//       userTouser: 0,
//       userTosponsor: 0,
//     },
//     referrerCoins: {
//       sponsorTosponsor: 0,
//       sponsorTouser: 0,
//       userTouser: 0,
//       userTosponsor: 0,
//     },
//     generalCategory: [],
//     ageCategory: [],
//     followersCategory: [],
//     genderCategory: [],
//     isHashtagsEditable: false,
//     isCaptionEditable: false,
//     isSponsorTagsEditable: false,
//     eventPrice: 0,
//     eventCaption1: "",
//     eventCaption2: "",
//     eventHashtags: [],
//     eventImageUrl: null,
//     eventAdditionalImages: [],
//     eventAdditionalDesc: "",
//     advideoUrl: null,
//     demoVideoUrl: null,
//     participationPost: {
//       caption1: "",
//       caption2: "",
//       hashtags: [],
//       frameImage: null,
//       carouselImages: [],
//       isPPHashtagEditable: false,
//       isPPCaptionEditable: false,
//       arePPSponsorsTagEditable: false,
//       isDefaultChecked: false,
//     },
//   };

//   // State Initialization

//   const [winners, setWinners] = useState(defaultAdditionalData.winners);
//   const [entities, setEntities] = useState(defaultAdditionalData.entities);
//   const [coinValue, setCoinValue] = useState(defaultAdditionalData.coinValue);
//   const [votes, setVotes] = useState(
//     additionalData?.votes || defaultAdditionalData.votes
//   );
//   const [referredCoins, setReferredCoins] = useState(
//     defaultAdditionalData.referredCoins
//   );
//   const [referrerCoins, setReferrerCoins] = useState(
//     defaultAdditionalData.referrerCoins
//   );
//   const [generalCategory, setGeneralCategory] = useState(
//     defaultAdditionalData.generalCategory
//   );
//   const [ageCategory, setAgeCategory] = useState(
//     defaultAdditionalData.ageCategory
//   );
//   const [followersCategory, setFollowersCategory] = useState(
//     defaultAdditionalData.followersCategory
//   );
//   const [genderCategory, setGenderCategory] = useState(
//     defaultAdditionalData.genderCategory
//   );
//   const [isHashtagsEditable, setIsHashtagsEditable] = useState(
//     defaultAdditionalData.isHashtagsEditable
//   );
//   const [isCaptionEditable, setIsCaptionEditable] = useState(
//     defaultAdditionalData.isCaptionEditable
//   );
//   const [isSponsorTagsEditable, setIsSponsorTagsEditable] = useState(
//     defaultAdditionalData.isSponsorTagsEditable
//   );
//   const [eventPrice, setEventPrice] = useState(
//     defaultAdditionalData.eventPrice
//   );
//   const [eventCaption1, setEventCaption1] = useState(
//     defaultAdditionalData.eventCaption1
//   );
//   const [eventCaption2, setEventCaption2] = useState(
//     defaultAdditionalData.eventCaption2
//   );

//   const [eventHashtags, setEventHashtags] = useState(
//     defaultAdditionalData.eventHashtags
//   );

//   const [eventImageUrl, setEventImageUrl] = useState(
//     defaultAdditionalData.eventImageUrl
//   );
//   const [eventAdditionalImages, setEventAdditionalImages] = useState(
//     defaultAdditionalData.eventAdditionalImages
//   );
//   const [eventAdditionalDesc, setEventAdditionalDesc] = useState(
//     defaultAdditionalData.eventAdditionalDesc
//   );
//   const [advideoUrl, setAdVideoUrl] = useState(
//     defaultAdditionalData.advideoUrl
//   );
//   const [demoVideoUrl, setDemoVideoUrl] = useState(
//     defaultAdditionalData.demoVideoUrl
//   );

//   // Participation Data
//   const participationPost = defaultAdditionalData.participationPost;
//   const [ppCaption1, setPpCaption1] = useState(participationPost.caption1);
//   const [ppCaption2, setPpCaption2] = useState(participationPost.caption2);
//   const [ppHashtags, setPpHashtags] = useState(participationPost.hashtags);
//   const [newHashtag, setNewHashtag] = useState("");
//   const [frameImageUrl, setFrameImageUrl] = useState(
//     participationPost.frameImage
//   );
//   const [participationCarouselImages, setParticipationCarouselImages] =
//     useState(participationPost.carouselImages);
//   const [isPPHashtagEditable, setIsPPHashtagEditable] = useState(
//     participationPost.isPPHashtagEditable
//   );
//   const [isPPCaptionEditable, setIsPPCaptionEditable] = useState(
//     participationPost.isPPCaptionEditable
//   );
//   const [arePPSponsorsTagEditable, setArePPSponsorsTagEditable] = useState(
//     participationPost.arePPSponsorsTagEditable
//   );
//   const [isDefaultChecked, setIsDefaultChecked] = useState(
//     participationPost.isDefaultChecked
//   );

//   // Update state when additionalData changes in edit mode
//   useEffect(() => {
//     if (isEditMode && additionalData) {
//       const updatedData = { ...defaultAdditionalData, ...additionalData };
//       setVotes(additionalData.votes || defaultAdditionalData.votes);
//       setWinners(updatedData.winners);
//       setEntities(updatedData.entities);
//       setCoinValue(updatedData.coinValue);
//       setReferredCoins(updatedData.referredCoins);
//       setReferrerCoins(updatedData.referrerCoins);
//       setGeneralCategory(updatedData.generalCategory);
//       setAgeCategory(updatedData.ageCategory);
//       setFollowersCategory(updatedData.followersCategory);
//       setGenderCategory(updatedData.genderCategory);
//       setIsHashtagsEditable(updatedData.isHashtagsEditable);
//       setIsCaptionEditable(updatedData.isCaptionEditable);
//       setIsSponsorTagsEditable(updatedData.isSponsorTagsEditable);
//       setEventPrice(updatedData.eventPrice);
//       setEventCaption1(updatedData.eventCaption1);
//       setEventCaption2(updatedData.eventCaption2);
//       setEventHashtags(updatedData.eventHashtags);
//       setEventImageUrl(updatedData.eventImageUrl);
//       setEventAdditionalImages(updatedData.eventAdditionalImages);
//       setEventAdditionalDesc(updatedData.eventAdditionalDesc);
//       setAdVideoUrl(updatedData.advideoUrl);
//       setDemoVideoUrl(updatedData.demoVideoUrl);

//       const participationPost = updatedData.participationPost || {};
//       setPpCaption1(participationPost.caption1);
//       setPpCaption2(participationPost.caption2);
//       setPpHashtags(participationPost.hashtags);
//       setFrameImageUrl(participationPost.frameImage);
//       setParticipationCarouselImages(participationPost.carouselImages);
//       setIsPPHashtagEditable(participationPost.isPPHashtagEditable);
//       setIsPPCaptionEditable(participationPost.isPPCaptionEditable);
//       setArePPSponsorsTagEditable(participationPost.arePPSponsorsTagEditable);
//       setIsDefaultChecked(participationPost.isDefaultChecked);
//     }
//   }, [additionalData, isEditMode]);

//   // Update setAdditionalData whenever relevant states change
//   useEffect(() => {
//     setAdditionalData({
//       votes,
//       winners,
//       entities,
//       coinValue,
//       referredCoins,
//       referrerCoins,
//       generalCategory,
//       ageCategory,
//       followersCategory,
//       genderCategory,
//       isHashtagsEditable,
//       isCaptionEditable,
//       isSponsorTagsEditable,
//       eventPrice,
//       eventCaption1,
//       eventCaption2,
//       eventHashtags,
//       eventImageUrl,
//       eventAdditionalImages,
//       eventAdditionalDesc,
//       advideoUrl,
//       demoVideoUrl,
//       participationPost: {
//         caption1: ppCaption1,
//         caption2: ppCaption2,
//         hashtags: ppHashtags,
//         frameImage: frameImageUrl,
//         carouselImages: participationCarouselImages,
//         isPPHashtagEditable,
//         isPPCaptionEditable,
//         arePPSponsorsTagEditable,
//         isDefaultChecked,

//       },
//     });
//   }, [
//     votes,
//     winners,
//     entities,
//     coinValue,
//     referredCoins,
//     referrerCoins,
//     generalCategory,
//     ageCategory,
//     followersCategory,
//     genderCategory,
//     isHashtagsEditable,
//     isCaptionEditable,
//     isSponsorTagsEditable,
//     eventPrice,
//     eventCaption1,
//     eventCaption2,
//     eventHashtags,
//     eventImageUrl,
//     eventAdditionalImages,
//     eventAdditionalDesc,
//     advideoUrl,
//     demoVideoUrl,
//     ppCaption1,
//     ppCaption2,
//     ppHashtags,
//     frameImageUrl,
//     participationCarouselImages,
//     isPPHashtagEditable,
//     isPPCaptionEditable,
//     arePPSponsorsTagEditable,
//     isDefaultChecked,
//   ]);

//   const handleFileUpload = async (file) => {
//     try {
//       if (!file) throw new Error("No file provided");

//       // Simulate file upload
//       const uploadResponse = await uploadFile(file); // Assuming uploadFile is a utility function
//       return uploadResponse; // Return the uploaded file URL or identifier
//     } catch (error) {
//       console.error("File upload failed:", error);
//       return null;
//     }
//   };

//   return (
//     <div className="p-4 border rounded-md mt-4 mb-10 bg-white shadow-xl">
//       <h2 className="text-xl lg:text-2xl font-bold text-blue-600 mb-6">
//         {isEditMode
//           ? "Edit Event Additional Data"
//           : "Add Event Additional Data"}
//       </h2>
//       <EventDataSection
//         handleFileUpload={handleFileUpload}
//         isHashtagsEditable={isHashtagsEditable}
//         setIsHashtagsEditable={setIsHashtagsEditable}
//         isCaptionEditable={isCaptionEditable}
//         setIsCaptionEditable={setIsCaptionEditable}
//         isSponsorTagsEditable={isSponsorTagsEditable}
//         setIsSponsorTagsEditable={setIsSponsorTagsEditable}
//         eventPrice={eventPrice}
//         setEventPrice={setEventPrice}
//         eventCaption1={eventCaption1}
//         setEventCaption1={setEventCaption1}
//         eventCaption2={eventCaption2}
//         setEventCaption2={setEventCaption2}
//         eventHashtags={eventHashtags}
//         setEventHashtags={setEventHashtags}
//         eventImageUrl={eventImageUrl}
//         setEventImageUrl={setEventImageUrl}
//         eventAdditionalImages={eventAdditionalImages}
//         setEventAdditionalImages={setEventAdditionalImages}
//         eventAdditionalDesc={eventAdditionalDesc}
//         setEventAdditionalDesc={setEventAdditionalDesc}
//         advideoUrl={advideoUrl}
//         setAdVideoUrl={setAdVideoUrl}
//         demoVideoUrl={demoVideoUrl}
//         setDemoVideoUrl={setDemoVideoUrl}
//         isEditMode={isEditMode}
//       />
//       <br />
//       <ParticipationAdditionalData
//           ppCaption1={ppCaption1}
//           setPpCaption1={setPpCaption1}
//           ppCaption2={ppCaption2}
//           setPpCaption2={setPpCaption2}
//         ppHashtags={ppHashtags}
//         setPpHashtags={setPpHashtags}
//         newHashtag={newHashtag}
//         setNewHashtag={setNewHashtag}
//         frameImageUrl={frameImageUrl}
//         setFrameImageUrl={setFrameImageUrl}
//         participationCarouselImages={participationCarouselImages}
//         setParticipationCarouselImages={setParticipationCarouselImages}
//         isPPHashtagEditable={isPPHashtagEditable}
//         setIsPPHashtagEditable={setIsPPHashtagEditable}
//         isPPCaptionEditable={isPPCaptionEditable}
//         setIsPPCaptionEditable={setIsPPCaptionEditable}
//         arePPSponsorsTagEditable={arePPSponsorsTagEditable}
//         setArePPSponsorsTagEditable={setArePPSponsorsTagEditable}
//         isDefaultChecked={isDefaultChecked}
//         setIsDefaultChecked={setIsDefaultChecked}
//         handleFileUpload={handleFileUpload}
//         isEditMode={isEditMode}
//       />
//       <Winners
//         winners={winners}
//         setWinners={setWinners}
//         isEditMode={isEditMode}
//       />
//       <Entities
//         entities={entities}
//         setEntities={setEntities}
//         isEditMode={isEditMode}
//       />
//       <Category
//         title="General Category"
//         categories={generalCategory}
//         setCategories={setGeneralCategory}
//         isEditMode={isEditMode}
//       />
//       <Category
//         title="Age Category"
//         categories={ageCategory}
//         setCategories={setAgeCategory}
//         isEditMode={isEditMode}
//       />
//       <Category
//         title="Followers Count Category"
//         categories={followersCategory}
//         setCategories={setFollowersCategory}
//         isEditMode={isEditMode}
//       />
//       <Category
//         title="Gender Category"
//         categories={genderCategory}
//         setCategories={setGenderCategory}
//         options={["All", "Male", "Female", "Others", "Open" ]}
//         isDropdown
//         isEditMode={isEditMode}
//       />

//       <CoinValue coinValue={coinValue} setCoinValue={setCoinValue} />
//       <ReferralCoins
//         referredCoins={referredCoins}
//         setReferredCoins={setReferredCoins}
//         referrerCoins={referrerCoins}
//         setReferrerCoins={setReferrerCoins}
//         isEditMode={isEditMode}
//       />
//       <VotesValue votes={votes} setVotes={setVotes} />
//     </div>
//   );
// };

// export default EventAdditionalData;
